import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Clock, User, Calendar } from "lucide-react";
import type { TelemetryNote, UpdateTelemetryNoteRequest } from "@/types";
import {
  formatTimeOffset,
  getRelativeTimeDescription,
} from "@/hooks/useTelemetryNotes";
import { formatUserName } from "@/utils/userName";

interface TelemetryNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  note?: TelemetryNote | null;
  timeOffset?: number | null;
  onCreateNote?: (content: string) => void;
  onUpdateNote?: (noteData: UpdateTelemetryNoteRequest) => void;
  onDeleteNote?: () => void;
  isCreating?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  isCreateMode?: boolean;
  startInEditMode?: boolean;
}

export const TelemetryNoteModal: React.FC<TelemetryNoteModalProps> = ({
  isOpen,
  onClose,
  note,
  timeOffset,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
  isCreateMode = false,
  startInEditMode = false,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const [content, setContent] = useState("");
  const [editTimeOffset, setEditTimeOffset] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [wasUpdating, setWasUpdating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isCreateMode && timeOffset !== undefined) {
        setContent("");
        setEditTimeOffset(timeOffset || 0);
        setIsEditing(false);
      } else if (note) {
        setContent(note.content);
        setEditTimeOffset(note.timeOffset);
        setIsEditing(startInEditMode);
      }
    }
  }, [isOpen, isCreateMode, note, timeOffset, startInEditMode]);

  // Track updating state changes
  useEffect(() => {
    if (isUpdating) {
      setWasUpdating(true);
    } else if (wasUpdating && !isUpdating && !isCreateMode) {
      // Update completed, return to view mode
      setIsEditing(false);
      setWasUpdating(false);
    }
  }, [isUpdating, wasUpdating, isCreateMode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (content.trim()) {
      if (isCreateMode && onCreateNote) {
        onCreateNote(content.trim());
      } else if (note && onUpdateNote) {
        const updateData: UpdateTelemetryNoteRequest = {};
        if (content.trim() !== note.content) {
          updateData.content = content.trim();
        }
        if (editTimeOffset !== note.timeOffset) {
          updateData.timeOffset = editTimeOffset;
        }
        onUpdateNote(updateData);
      }
    }
  };

  const handleDelete = () => {
    if (
      onDeleteNote &&
      window.confirm(t("pages:sessions.telemetry.notes.confirmDelete"))
    ) {
      onDeleteNote();
    }
  };

  const displayTimeOffset = isCreateMode ? timeOffset : note?.timeOffset;
  const canEdit = !isCreateMode && note;
  const isLoading = isCreating || isUpdating || isDeleting;

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 sm:items-center">
  <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md sm:mx-4 max-h-[90vh] overflow-hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {/* Header */}
 <div className="flex items-center justify-between p-4 border-b border-secondary-200">
 <h3 className="text-lg font-semibold text-secondary-900">
            {isCreateMode
              ? t("pages:sessions.telemetry.notes.addNote")
              : isEditing
                ? t("pages:sessions.telemetry.notes.editNote")
                : t("pages:sessions.telemetry.notes.noteDetails")}
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
 className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
 <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
 <div className="p-4">
          {/* Time Information */}
 <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
 <div className="flex items-center gap-2 text-sm text-secondary-600 mb-1">
 <Clock className="h-4 w-4" />
              <span>
                {t("pages:sessions.telemetry.notes.timeLabel")}
                {formatTimeOffset(displayTimeOffset || 0)}
              </span>
            </div>
            <div className="text-xs text-secondary-500">
              {t(
                `pages:sessions.telemetry.notes.${getRelativeTimeDescription(
                  displayTimeOffset || 0,
                )}`,
              )}
            </div>

            {/* Time offset editing for existing notes */}
            {isEditing && canEdit && (
 <div className="mt-2">
              <label className="block text-xs font-medium text-secondary-700 mb-1">
                {t("pages:sessions.telemetry.notes.adjustTimeSeconds")}
              </label>
                <input
                  type="number"
                  step="0.01"
                  value={editTimeOffset.toFixed(2)}
                  onChange={(e) =>
                    setEditTimeOffset(parseFloat(e.target.value) || 0)
                  }
 className="w-full px-2 py-1 text-sm border border-secondary-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}
          </div>

          {/* Author info for existing notes */}
          {note && !isCreateMode && (
 <div className="mb-4 flex items-center gap-2 text-sm text-secondary-600">
 <User className="h-4 w-4" />
              <span>
                {formatUserName(note.user)}
              </span>
              {note.createdAt && (
                <>
 <Calendar className="h-4 w-4 ml-2" />
                  <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                </>
              )}
            </div>
          )}

          {/* Note content form */}
          <form onSubmit={handleSubmit}>
 <div className="mb-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                {t("pages:sessions.telemetry.notes.content")}
              </label>
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t(
                  "pages:sessions.telemetry.notes.contentPlaceholder",
                )}
                rows={4}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isLoading || (!isCreateMode && !isEditing)}
                required
              />
              <div className="text-xs text-secondary-500 mt-1">
                {content.length}
                {t("pages:sessions.telemetry.notes.characterCount")}
              </div>
            </div>

            {/* Action buttons */}
 <div className="flex gap-2 justify-end">
              {/* Cancel */}
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
 className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {t("common:cancel")}
              </button>

              {/* Edit toggle for existing notes */}
              {canEdit && !isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading}
 className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-lg hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {t("pages:sessions.telemetry.notes.edit")}
                </button>
              )}

              {/* Delete button for existing notes */}
              {canEdit && onDeleteNote && !isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isLoading}
 className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isDeleting
                    ? t("pages:sessions.deleting")
                    : t("common:delete")}
                </button>
              )}

              {/* Save button */}
              {(isCreateMode || isEditing) && (
                <button
                  type="submit"
                  disabled={isLoading || !content.trim()}
 className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {isLoading
                    ? isCreateMode
                      ? t("common:creating")
                      : t("common:saving")
                    : isCreateMode
                      ? t("pages:sessions.telemetry.notes.createNote")
                      : t("pages:sessions.telemetry.notes.saveChanges")}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
